import { Minus, Plus } from 'lucide-react'
import type { Category, ContentsEstimate } from '../types'
import { money } from '../format'

export function ContentsPicker({
  categories,
  quantities,
  onChange,
  estimate,
  pending,
  error,
}: {
  categories: Category[]
  quantities: Record<string, number>
  onChange: (code: string, quantity: number) => void
  estimate: ContentsEstimate | null
  pending: boolean
  error: string
}) {
  const count = Object.values(quantities).reduce(
    (sum, n) => sum + (Number.isFinite(n) ? n : 0),
    0,
  )
  return (
    <section className="contents-picker" aria-labelledby="contents-title">
      <header>
        <h2 id="contents-title">What’s in your package?</h2>
        <p>
          Count bottles or sealed retail packages, not individual tablets. You
          can include more than one type in a parcel.
        </p>
      </header>
      <div className="contents-picker__rows">
        {categories.map((category) => {
          const quantity = quantities[category.code] ?? 0
          return (
            <div
              className={`contents-choice ${quantity > 0 ? 'is-selected' : ''}`}
              key={category.code}
            >
              <div>
                <h3>{category.displayName}</h3>
                <p>
                  {category.code === 'collector-beads'
                    ? 'Packages of collectible beads'
                    : category.code.startsWith('oral')
                    ? 'Bottles or sealed tablet/capsule packages'
                    : 'Bottles of topical serum'}
                </p>
                <small>
                  Per unit: {category.customsItemWeightGrams} g ·{' '}
                  {money(category.unitValueMinor, category.currency)} declared
                  value
                </small>
              </div>
              <div className="quantity-control">
                <button
                  type="button"
                  aria-label={`Remove one ${category.displayName}`}
                  disabled={!quantity}
                  onClick={() =>
                    onChange(category.code, Math.max(0, quantity - 1))
                  }
                >
                  <Minus size={18} />
                </button>
                <input
                  type="number"
                  min="0"
                  step="1"
                  inputMode="numeric"
                  aria-label={`${category.displayName} quantity`}
                  value={Number.isNaN(quantity) ? '' : quantity}
                  onChange={(e) =>
                    onChange(
                      category.code,
                      e.target.value === '' ? NaN : Number(e.target.value),
                    )
                  }
                />
                <button
                  type="button"
                  aria-label={`Add one ${category.displayName}`}
                  onClick={() => onChange(category.code, (quantity || 0) + 1)}
                >
                  <Plus size={18} />
                </button>
              </div>
            </div>
          )
        })}
      </div>
      <div className="contents-estimate" aria-live="polite" aria-busy={pending}>
        <div className="contents-estimate__head">
          <b>One combined package</b>
          <span>
            {count > 0
              ? `${count} ${count === 1 ? 'unit' : 'units'}`
              : 'Choose your quantities'}
          </span>
        </div>
        {estimate ? (
          <>
            <dl>
              <div>
                <dt>Estimated packed weight</dt>
                <dd>{estimate.parcel.weightGrams} g</dd>
              </div>
              <div>
                <dt>Estimated box size</dt>
                <dd>
                  {estimate.parcel.lengthMm / 10} ×{' '}
                  {estimate.parcel.widthMm / 10} ×{' '}
                  {estimate.parcel.heightMm / 10} cm
                </dd>
              </div>
              <div>
                <dt>Total declared value</dt>
                <dd>
                  {money(
                    estimate.items.reduce(
                      (sum, item) => sum + item.quantity * item.unitValueMinor,
                      0,
                    ),
                    estimate.items[0].currency,
                  )}
                </dd>
              </div>
            </dl>
            <p>
              Based on our standard package presets. We weigh and measure the
              actual parcel before shipping.
            </p>
          </>
        ) : (
          <p>
            {error ||
              (pending
                ? 'Calculating your package…'
                : 'Add at least one bottle or package to see your estimate.')}
          </p>
        )}
      </div>
    </section>
  )
}
